import {
  BulbOutlined,
  EditOutlined,
  FileImageOutlined,
  FireOutlined,
  FormOutlined,
  InboxOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Modal,
  message,
  Radio,
  Segmented,
  Slider,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { polishPrompt } from '@/services/promptPolish';
import { getOssStsToken } from '@/services/ossSts';
import OSS from 'ali-oss';
import './index.less';

export type VideoGenMode = 'text' | 'video';
export type VideoGenRatio = 'vertical' | 'horizontal';

export interface VideoGenModelOption {
  value: string;
  label: React.ReactNode;
}

export interface GenRatioOption {
  value: string;
  label: React.ReactNode;
  desc?: React.ReactNode;
}

export interface GenSizeOption {
  value: string;
  label: React.ReactNode;
}

export interface PromptTemplateItem {
  label: string;
  value: string;
}

export interface VideoGenCardProps {
  title: string;
  subtitle?: string;
  providerTag?: string;
  /** 媒体类型：视频 or 图片；影响提示词润色文案，默认 video */
  mediaKind?: 'video' | 'image';
  modelTitle?: string;
  modelOptions: VideoGenModelOption[];
  modelLayout?: 'row' | 'grid';
  modelGridMinWidth?: number;
  defaultModelValue?: string;
  showModeToggle?: boolean;
  showUpload?: boolean;
  promptTitle?: string;
  promptPlaceholder?: string;
  ratioTitle?: string;
  ratioLayout?: 'row' | 'grid';
  ratioOptions?: GenRatioOption[];
  defaultRatioValue?: string;
  showSize?: boolean;
  sizeTitle?: string;
  sizeOptions?: GenSizeOption[];
  getSizeOptions?: (modelKey: string) => GenSizeOption[];
  defaultSizeValue?: string;
  templates?: PromptTemplateItem[];
  generateText?: string;
  /** 用于“创作同款”等跳转预填充 */
  initialPrompt?: string;
  initialUrls?: string[];
  initialModelKey?: string;
  initialRatioKey?: string;
  initialSizeKey?: string;
  onGenerate?: (payload: {
    mode: VideoGenMode;
    modelKey: string;
    ratio: VideoGenRatio;
    ratioKey: string;
    sizeKey: string;
    count: number;
    prompt: string;
    polishedPrompt: string;
    urls?: string[];
  }) => Promise<void> | void;
}

export const VideoGenCard: React.FC<VideoGenCardProps> = ({
  title,
  subtitle = '秒级响应 · 自动配音 · 智能优化',
  providerTag = 'Google 模型',
  mediaKind = 'video',
  modelTitle = '模型选择',
  modelOptions,
  modelLayout = 'row',
  modelGridMinWidth = 120,
  defaultModelValue,
  showModeToggle = true,
  showUpload = true,
  promptTitle = '提示词',
  promptPlaceholder = '根据主题描述生成内容，描述生成的场景、主角、一镜到底等…',
  ratioTitle = '视频比例',
  ratioLayout = 'row',
  ratioOptions,
  defaultRatioValue,
  showSize = false,
  sizeTitle = '图片尺寸',
  sizeOptions,
  getSizeOptions,
  defaultSizeValue,
  templates,
  generateText = '生成视频',
  initialPrompt,
  initialUrls,
  initialModelKey,
  initialRatioKey,
  initialSizeKey,
  onGenerate,
}) => {
  const [mode, setMode] = useState<VideoGenMode>('video');
  const [modelKey, setModelKey] = useState<string>(
    initialModelKey ?? defaultModelValue ?? modelOptions[0]?.value ?? 'default',
  );
  const [ratio, setRatio] = useState<VideoGenRatio>('vertical');
  const [ratioKey, setRatioKey] = useState<string>(
    initialRatioKey ?? defaultRatioValue ?? ratioOptions?.[0]?.value ?? 'default',
  );
  const [sizeKey, setSizeKey] = useState<string>(
    initialSizeKey ?? defaultSizeValue ?? sizeOptions?.[0]?.value ?? 'default',
  );
  const [count, setCount] = useState<number>(1);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>(initialUrls ?? []);
  const [prompt, setPrompt] = useState<string>(initialPrompt ?? '');
  const [generating, setGenerating] = useState<boolean>(false);

  const [polishOpen, setPolishOpen] = useState<boolean>(false);
  const [polishing, setPolishing] = useState<boolean>(false);
  const [polishStyle, setPolishStyle] = useState<
    'pro' | 'cinema' | 'creative' | 'concise'
  >('pro');

  const effectiveSizeOptions = useMemo(() => {
    const fromFn = getSizeOptions?.(modelKey);
    if (Array.isArray(fromFn) && fromFn.length > 0) return fromFn;
    return sizeOptions ?? [];
  }, [getSizeOptions, modelKey, sizeOptions]);

  useEffect(() => {
    if (!showSize) return;
    if (!effectiveSizeOptions || effectiveSizeOptions.length === 0) return;
    const ok = effectiveSizeOptions.some((o) => o.value === sizeKey);
    if (!ok) {
      setSizeKey(effectiveSizeOptions[0].value);
    }
  }, [effectiveSizeOptions, showSize, sizeKey]);

  useEffect(() => {
    if (initialPrompt != null) setPrompt(initialPrompt);
    // 只在传入时覆盖，避免用户手动改了又被刷新掉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  useEffect(() => {
    if (Array.isArray(initialUrls)) {
      setUploadedUrls(initialUrls);
      const prefills = initialUrls
        .map((url, idx) => String(url || '').trim())
        .filter(Boolean)
        .slice(0, 2)
        .map((url, idx) => ({
          uid: `prefill-${idx}`,
          name: `参考图${idx + 1}`,
          status: 'done' as const,
          url,
          thumbUrl: url,
        }));
      setFileList(prefills as UploadFile[]);
    }
  }, [initialUrls]);

  useEffect(() => {
    if (initialModelKey) setModelKey(initialModelKey);
  }, [initialModelKey]);

  useEffect(() => {
    if (initialRatioKey) setRatioKey(initialRatioKey);
  }, [initialRatioKey]);

  useEffect(() => {
    if (initialSizeKey) setSizeKey(initialSizeKey);
  }, [initialSizeKey]);

  const estimatedText = useMemo(() => {
    // mock：按模型 key 简单估算
    if (String(modelKey).includes('4k') || String(modelKey).includes('15')) return '预计 60 秒';
    return '预计 40 秒';
  }, [modelKey]);

  const canGenerate = useMemo(() => {
    return !!prompt.trim();
  }, [prompt]);

  const uploadToOss = async (file: File) => {
    const sts = await getOssStsToken();
    const ossRegion = sts.region?.startsWith('oss-') ? sts.region : `oss-${sts.region}`;
    const client = new OSS({
      region: ossRegion,
      endpoint: sts.endpoint,
      accessKeyId: sts.accessKeyId,
      accessKeySecret: sts.accessKeySecret,
      stsToken: sts.securityToken,
      bucket: sts.bucket,
      secure: true,
    } as any);

    const isVideo = file.type.startsWith('video/');
    const dir = `reference_catalog/${isVideo ? 'video' : 'image'}`;
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    const key = `${dir}/${Date.now()}_${Math.random().toString(16).slice(2)}${ext ? '.' + ext : ''}`;
    await client.put(key, file);
    return `https://${sts.bucket}.${ossRegion}.aliyuncs.com/${key}`;
  };

  const polishedPrompt = useMemo(() => {
    return prompt;
  }, [polishStyle, prompt]);

  return (
    <div className="video-gen-card">
      <div className="video-gen-title">{title}</div>
      <div className="video-gen-subtitle">{subtitle}</div>

      <div className="video-gen-topbar">
        {showModeToggle ? (
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as VideoGenMode)}
            options={[
              {
                label: (
                  <span className="video-gen-seg">
                    <FormOutlined />
                    文字视频
                  </span>
                ),
                value: 'text',
              },
              {
                label: (
                  <span className="video-gen-seg">
                    <FileImageOutlined />
                    图生视频
                  </span>
                ),
                value: 'video',
              },
            ]}
          />
        ) : (
          <div />
        )}
        <Tag color="purple" style={{ marginLeft: 8 }}>
          {providerTag}
        </Tag>
      </div>

      <Form layout="vertical">
        {showUpload && mode === 'video' && (
          <div className="video-gen-section video-gen-section-upload">
            <div className="video-gen-field-head">
              <Typography.Text strong>参考图像</Typography.Text>
              <Typography.Text type="secondary">可选</Typography.Text>
            </div>
            <div className="video-gen-upload-area">
              <Upload.Dragger
                accept="image/png,image/jpeg"
                fileList={fileList}
                listType="picture"
                maxCount={2}
                beforeUpload={() => false}
                onChange={async (info) => {
                  const next = info.fileList.slice(0, 2);
                  setFileList(next);
                  const newly = next.filter((f) => !f.url && !!f.originFileObj);
                  if (newly.length === 0) return;
                  for (const f of newly) {
                    try {
                      // @ts-ignore
                      const raw: File | undefined = f.originFileObj;
                      if (!raw) continue;
                      const url = await uploadToOss(raw);
                      f.url = url;
                      f.thumbUrl = url;
                      setUploadedUrls((prev) => Array.from(new Set([...prev, url])));
                      setFileList((prev) => prev.map((x) => (x.uid === f.uid ? { ...x, url, thumbUrl: url } : x)));
                    } catch (e) {
                      message.error('上传失败，请重试');
                    }
                  }
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">上传图片</p>
                <p className="ant-upload-hint">支持 jpg/png 格式，最大 5MB</p>
              </Upload.Dragger>
            </div>
            {
              mediaKind === 'video' && (
                <div className="video-gen-upload-tip">
                  可上传 1-2 张图片，单图时第一张做首帧；双图时两张分别为首尾帧
                </div>
              ) 
            }
          </div>
        )}

        <div className="video-gen-section video-gen-section-prompt">
          <div className="video-gen-prompt-label">
            <Typography.Text strong>{promptTitle}</Typography.Text>
            <Space size={8}>
              <Button size="small" icon={<UploadOutlined />}>
                模板文案
              </Button>
              <Tooltip title="对当前提示词进行润色与补全，使生成效果更稳定。">
                <Button
                  size="small"
                  type="primary"
                  ghost
                  onClick={() => setPolishOpen(true)}
                  disabled={!prompt.trim()}
                >
                  AI 润色
                </Button>
              </Tooltip>
            </Space>
          </div>
          <Input.TextArea
            placeholder={promptPlaceholder}
            rows={4}
            value={prompt}
            maxLength={5000}
            showCount
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        <div className="video-gen-section video-gen-section-model">
          <Typography.Text strong>{modelTitle}</Typography.Text>
          <div className="video-gen-card-radio">
            <Radio.Group value={modelKey} onChange={(e) => setModelKey(e.target.value)}>
              {modelLayout === 'grid' ? (
                <div
                  className="video-gen-model-grid"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(${modelGridMinWidth}px, 1fr))`,
                  }}
                >
                  {modelOptions.map((opt) => (
                    <Radio.Button
                      key={opt.value}
                      value={opt.value}
                      className="video-gen-radio-item"
                    >
                      {opt.label}
                    </Radio.Button>
                  ))}
                </div>
              ) : (
                <div className="video-gen-row-wrap">
                  {modelOptions.map((opt) => (
                    <Radio.Button
                      key={opt.value}
                      value={opt.value}
                      className="video-gen-radio-item"
                    >
                      {opt.label}
                    </Radio.Button>
                  ))}
                </div>
              )}
            </Radio.Group>
          </div>
        </div>

        <div className="video-gen-section video-gen-section-ratio">
          <Typography.Text strong>{ratioTitle}</Typography.Text>
          <div className="video-gen-card-radio">
            {ratioOptions && ratioOptions.length > 0 ? (
              <Radio.Group value={ratioKey} onChange={(e) => setRatioKey(e.target.value)}>
                {ratioLayout === 'grid' ? (
                  <div className="video-gen-ratio-grid">
                    {ratioOptions.map((opt) => (
                      <Radio.Button
                        key={opt.value}
                        value={opt.value}
                        className="video-gen-radio-item video-gen-radio-card"
                      >
                        <div className="video-gen-radio-card-icon-box">
                          <div className="video-gen-radio-card-icon" data-ratio={String(opt.value)} />
                        </div>
                        <div className="video-gen-radio-card-label">{opt.label}</div>
                        {opt.desc ? (
                          <div className="video-gen-radio-card-desc">{opt.desc}</div>
                        ) : null}
                      </Radio.Button>
                    ))}
                  </div>
                ) : (
                  <div className="video-gen-row-wrap">
                    {ratioOptions.map((opt) => (
                      <Radio.Button
                        key={opt.value}
                        value={opt.value}
                        className="video-gen-radio-item"
                      >
                        {opt.label}
                      </Radio.Button>
                    ))}
                  </div>
                )}
              </Radio.Group>
            ) : (
              <Radio.Group value={ratio} onChange={(e) => setRatio(e.target.value)}>
                <div className="video-gen-row-wrap">
                  <Radio.Button value="vertical" className="video-gen-radio-item" style={{ flex: 1 }}>
                    竖屏
                  </Radio.Button>
                  <Radio.Button value="horizontal" className="video-gen-radio-item" style={{ flex: 1 }}>
                    横屏
                  </Radio.Button>
                </div>
              </Radio.Group>
            )}
          </div>
        </div>

        {showSize && effectiveSizeOptions && effectiveSizeOptions.length > 0 ? (
          <div className="video-gen-section video-gen-section-size">
            <Typography.Text strong>{sizeTitle}</Typography.Text>
            <div className="video-gen-card-radio">
              <Radio.Group value={sizeKey} onChange={(e) => setSizeKey(e.target.value)}>
                <div className="video-gen-row-wrap">
                  {effectiveSizeOptions.map((opt) => (
                    <Radio.Button
                      key={opt.value}
                      value={opt.value}
                      className="video-gen-radio-item"
                    >
                      {opt.label}
                    </Radio.Button>
                  ))}
                </div>
              </Radio.Group>
            </div>
          </div>
        ) : null}

        <div className="video-gen-section video-gen-section-count">
          <Typography.Text strong>生成数量</Typography.Text>
          <div className="video-gen-count-row">
            <Slider
              min={1}
              max={10}
              value={count}
              onChange={(v) => setCount(Number(v))}
              tooltip={{ formatter: (v) => `${v} 条` }}
            />
            <Typography.Text type="secondary" className="video-gen-count-value">
              {count} 条
            </Typography.Text>
          </div>
        </div>

        {Array.isArray(templates) && templates.length > 0 ? (
          <div className="video-gen-section video-gen-section-templates">
            <Typography.Text strong>快速模板</Typography.Text>
            <div className="video-gen-template-tags">
              <Space size={[8, 8]} wrap>
                {templates.map((t) => (
                  <Button key={t.label} size="small" onClick={() => setPrompt(t.value)}>
                    {t.label}
                  </Button>
                ))}
              </Space>
            </div>
          </div>
        ) : null}

        <div className="video-gen-section">
          <div className="video-gen-actionbar">
            <Button
              type="primary"
              block
              size="large"
              disabled={!canGenerate || generating}
              loading={generating}
              onClick={async () => {
                if (!canGenerate || !onGenerate) return;
                try {
                  setGenerating(true);
                  await onGenerate({
                    mode,
                    modelKey,
                    ratio,
                    ratioKey,
                    sizeKey,
                    count,
                    prompt,
                    polishedPrompt,
                    urls: uploadedUrls,
                  });
                } finally {
                  setGenerating(false);
                }
              }}
            >
              {generateText}
            </Button>
            <div className="video-gen-actionbar-meta">
              {canGenerate ? `预计耗时：${estimatedText}` : '请先完善必填信息'}
            </div>
          </div>
        </div>
      </Form>

      <Modal
        open={polishOpen}
        onCancel={() => setPolishOpen(false)}
        footer={null}
        width={920}
        destroyOnHidden
        className="video-gen-polish-modal"
      >
        <div className="video-gen-polish-head">
          <div className="video-gen-polish-icon">
            <EditOutlined />
          </div>
          <div>
            <div className="video-gen-polish-title">AI 提示词润色</div>
            <div className="video-gen-polish-subtitle">
              智能优化您的提示词描述，生成更专业的提示词
            </div>
          </div>
        </div>

        <div className="video-gen-polish-label">原始提示词</div>
        <div className="video-gen-polish-box">{prompt.trim() || '—'}</div>

        <div className="video-gen-polish-label">优化风格</div>
        <div className="video-gen-polish-style-grid">
          <button
            type="button"
            className={`video-gen-style-card${polishStyle === 'pro' ? ' active' : ''}`}
            onClick={() => setPolishStyle('pro')}
          >
            <div className="video-gen-style-card-icon">
              <FireOutlined />
            </div>
            <div className="video-gen-style-card-text">专业增强</div>
          </button>
          <button
            type="button"
            className={`video-gen-style-card${polishStyle === 'cinema' ? ' active' : ''}`}
            onClick={() => setPolishStyle('cinema')}
          >
            <div className="video-gen-style-card-icon">
              <VideoCameraOutlined />
            </div>
            <div className="video-gen-style-card-text">电影质感</div>
          </button>
          <button
            type="button"
            className={`video-gen-style-card${polishStyle === 'creative' ? ' active' : ''}`}
            onClick={() => setPolishStyle('creative')}
          >
            <div className="video-gen-style-card-icon">
              <BulbOutlined />
            </div>
            <div className="video-gen-style-card-text">创意扩展</div>
          </button>
          <button
            type="button"
            className={`video-gen-style-card${polishStyle === 'concise' ? ' active' : ''}`}
            onClick={() => setPolishStyle('concise')}
          >
            <div className="video-gen-style-card-icon">
              <EditOutlined />
            </div>
            <div className="video-gen-style-card-text">简洁精炼</div>
          </button>
        </div>

        <div className="video-gen-polish-footer">
          <Button onClick={() => setPolishOpen(false)}>取消</Button>
          <Button
            type="primary"
            loading={polishing}
            onClick={async () => {
              if (!prompt.trim()) {
                setPolishOpen(false);
                return;
              }
              try {
                setPolishing(true);
                const base = prompt.trim();
                const isImage = mediaKind === 'image';
                let metaPrompt: string;
                if (polishStyle === 'cinema') {
                  metaPrompt = isImage
                    ? '将以上图片描述优化为具有电影质感的提示词，强调 cinematic lighting, depth of field, color grading 等电影元素'
                    : '将以上视频描述优化为具有电影质感的提示词，强调 cinematic lighting, depth of field, color grading 等电影元素';
                } else if (polishStyle === 'creative') {
                  metaPrompt = isImage
                    ? '在以上图片描述基础上进行创意扩展，添加更多想象力元素、独特视角和艺术表现手法'
                    : '在以上视频描述基础上进行创意扩展，添加更多想象力元素、独特视角和艺术表现手法';
                } else if (polishStyle === 'concise') {
                  metaPrompt = isImage
                    ? '将以上图片描述优化为简洁精炼的提示词，保留核心要素，去除冗余描述'
                    : '将以上视频描述优化为简洁精炼的提示词，保留核心要素，去除冗余描述';
                } else {
                  metaPrompt = isImage
                    ? '将以上图片描述优化为更专业、详细的提示词，增加画面细节、光影效果等专业描述'
                    : '将以上视频描述优化为更专业、详细的提示词，增加画面细节、光影效果、镜头语言等专业描述';
                }
                const content = `${base}\n\n${metaPrompt} 直接返回优化后的内容 请勿添加任何其他内容`;

                const polished = await polishPrompt({
                  text: content,
                  scope: providerTag,
                  modelKey,
                });
                if (typeof polished === 'string' && polished.trim()) {
                  setPrompt(polished);
                }
                setPolishOpen(false);
              } catch (e) {
                // 失败时保留原文案，给个轻提示
                // 这里只用 message 避免打断主流程
                // eslint-disable-next-line no-console
                console.error('polish prompt failed', e);
              } finally {
                setPolishing(false);
              }
            }}
          >
            开始优化
          </Button>
        </div>
      </Modal>
    </div>
  );
};

